import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { fromEvent, merge, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, take, takeWhile, tap } from 'rxjs/operators';

import { AppState } from '../../../../../core/reducers';
import { ContactRelationshipsService } from '../../../../../core/lists';
import { StringUtilsService } from '../../../../../core/services/string-utils.service';
import { LayoutUtilsService, MessageType, QueryParamsModel } from '../../../../../core/_base/crud';
import { GeneralAutocompleteComponent } from '../../../../partials/layout/general-autocomplete/general-autocomplete.component';

import { Category, Document, Entity } from '../../models';
import { AccessHistoryType, AccessHistoryUserType, DocType, EntityType } from '../../enums';
import { DocumentService } from '../../services';
import { RequestDocumentPage } from '../../actions';
import { DocumentDataSource } from '../../data-sources';
import { selectDocumentActionLoading, selectDocumentError } from '../../selectors';
import { CreateDocumentComponent } from '../create-document/create-document.component';
import { ShareDocumentComponent } from '../share-document/share-document.component';
import { AccessHistoryListComponent } from '../access-history-list/access-history-list.component';

@Component({
  selector: 'kt-document-list',
  templateUrl: './document-list.component.html',
  styleUrls: ['./document-list.component.scss'],
})
export class DocumentListComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();
  dataSource: DocumentDataSource;
  docTypes = Object.values(DocType);
  displayedCols = [
    'id',
    'name',
    'description',
    'category_name',
    'entities',
    'type',
    'uploadDate',
    'requiresReview',
    'reviewed',
    'actions',
  ];
  searchBarCols = [
    'searchId',
    'searchName',
    'searchDescription',
    'searchCategory',
    'searchEntity',
    'searchType',
    'searchCreatedDate',
    'searchRequiresReview',
    'searchReviewed',
    'searchActions',
  ];
  showSearchBar = false;
  getCategoryAutocomplete = this.documentService.findCategories.bind(this.documentService, {});
  getEntityAutocomplete = this.relationshipsService.findEntities.bind(this.relationshipsService, {
    isActive: true,
    entityTypes: Object.values(EntityType),
  });
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  @ViewChild('searchIdInput') searchIdInput: ElementRef;
  @ViewChild('searchNameInput') searchNameInput: ElementRef;
  @ViewChild('searchDescriptionInput') searchDescriptionInput: ElementRef;
  @ViewChild('searchCategoryAutocomplete') searchCategoryAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('searchEntityAutocomplete') searchEntityAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('searchTypeSelect') searchTypeSelect: MatSelect;
  @ViewChild('searchCreatedDateInput') searchCreatedDateInput: ElementRef;
  @ViewChild('searchRequiresReviewSelect') searchRequiresReviewSelect: MatSelect;
  @ViewChild('searchReviewedSelect') searchReviewedSelect: MatSelect;
  @Input() entityId: number;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { docShareView: boolean },
    public stringUtils: StringUtilsService,
    private dialogRef: MatDialogRef<DocumentListComponent>,
    private dialog: MatDialog,
    private datePipe: DatePipe,
    private store: Store<AppState>,
    private translate: TranslateService,
    private layoutUtils: LayoutUtilsService,
    private permissionsService: NgxPermissionsService,
    private relationshipsService: ContactRelationshipsService,
    private documentService: DocumentService,
  ) {}

  ngOnInit(): void {
    this.dataSource = new DocumentDataSource(this.store, this.paginator);

    if (!this.entityId && localStorage.getItem('docsEntityId')) {
      this.entityId = +localStorage.getItem('docsEntityId');
      localStorage.removeItem('docsEntityId');
    }

    if (this.entityId) {
      const entitiesIdx = this.displayedCols.indexOf('entities');

      this.displayedCols.splice(entitiesIdx, 1);
      this.searchBarCols.splice(entitiesIdx, 1);
    }

    this.subscriptions.add(this.dataSource.entitySubject.subscribe());

    this.subscriptions.add(
      this.sort.sortChange.subscribe((res) => {
        this.paginator.pageIndex = 0;

        if (!res.direction) {
          this.sort.direction = '';
          this.sort.active = '';
        }
      }),
    );

    this.subscriptions.add(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(tap(() => this.loadDocumentList()))
        .subscribe(),
    );

    this.subscriptions.add(
      fromEvent(this.searchInput.nativeElement, 'keyup')
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          tap(() => this.reloadDocumentList()),
        )
        .subscribe(),
    );

    setTimeout(() => this.loadDocumentList());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  @HostListener('window:beforeunload')
  saveEntityId(): Promise<boolean> | boolean {
    localStorage.setItem('docsEntityId', `${this.entityId || 0}`);

    return true;
  }

  getFilterConfig() {
    const filter: any = {};
    const entityId = this.entityId || this.searchEntityAutocomplete?.value?.id;

    filter.entitiesIds = entityId ? [entityId] : [];

    if (this.showSearchBar) {
      filter.id = this.searchIdInput?.nativeElement?.value;
      filter.name = this.searchNameInput?.nativeElement?.value;
      filter.description = this.searchDescriptionInput?.nativeElement?.value;
      filter.categoryId = this.searchCategoryAutocomplete?.value?.id;
      filter.types = this.searchTypeSelect?.value;
      filter.created = this.datePipe.transform(
        this.searchCreatedDateInput?.nativeElement?.value,
        'yyyy-MM-dd',
      );
      filter.requiresReview = this.searchRequiresReviewSelect?.value;
      filter.reviewed = this.searchReviewedSelect?.value;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
    }

    return filter;
  }

  loadDocumentList() {
    const query = new QueryParamsModel(
      this.getFilterConfig(),
      this.sort.direction || 'desc',
      this.sort.active || 'id',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );

    this.store.dispatch(new RequestDocumentPage({ query }));
  }

  displayMessage(message: string, messageType: MessageType): void {
    this.store
      .pipe(
        select(selectDocumentActionLoading),
        takeWhile((isLoading) => isLoading === true),
        finalize(() => {
          this.store.pipe(select(selectDocumentError), take(1)).subscribe((err) => {
            if (err) {
              const status = err.status;

              if (status === 401 || status === 403 || status === 404) return;

              message = this.translate.instant('GLOBAL.UNSPECIFIED_ERROR');
              return this.layoutUtils.showActionNotification(
                message,
                messageType,
                3000,
                false,
                false,
              );
            }

            this.layoutUtils.showActionNotification(message, messageType, 3000, false, false);
            setTimeout(() => this.loadDocumentList(), 500);
          });
        }),
      )
      .subscribe();
  }

  getStatusClass(status: string, type: 'REQUIRE_REVIEW' | 'REVIEWED'): string {
    return status === 'true'
      ? 'success'
      : status === 'false'
      ? type === 'REQUIRE_REVIEW'
        ? 'secondary text-dark'
        : 'danger'
      : 'warning';
  }

  getStatusLabel(status: string, type: 'REQUIRE_REVIEW' | 'REVIEWED'): string {
    return status === 'true'
      ? type === 'REQUIRE_REVIEW'
        ? this.translate.instant('GLOBAL.REQUIRED')
        : this.translate.instant('GLOBAL.REVIEWED')
      : status === 'false'
      ? type === 'REQUIRE_REVIEW'
        ? this.translate.instant('GLOBAL.NOT_REQUIRED')
        : this.translate.instant('GLOBAL.NOT_REVIEWED')
      : this.translate.instant('GLOBAL.ALL');
  }

  formatCategoryOption(category: Category): string {
    return category?.name || '';
  }

  formatEntityOption(entity: Entity): string {
    return entity?.id
      ? `${
          entity.name || `${entity.first_name || ''} ${entity.last_name || ''}`
        }<small class="text-muted ml-2">(${entity.entity_type || ''})</small>`.trim()
      : '';
  }

  formatEntityValue(entity: Entity): string {
    return entity?.id
      ? `${entity.name || `${entity.first_name || ''} ${entity.last_name || ''}`}`.trim()
      : '';
  }

  getEntitiesString(entities: Entity[], startIdx = 0): string {
    return entities
      .slice(startIdx)
      .map((e) => this.formatEntityValue(e))
      .join(', ');
  }

  addDocument(event: Event): void {
    event.stopPropagation();

    const document = new Document();
    this.updateDocument(null, document);
  }

  updateDocument(event: Event, doc: Document): void {
    event?.stopPropagation();

    const document = { ...doc };

    if (this.entityId) document.entity_id = this.entityId;

    let sessionId = 0;
    const message = this.translate.instant(
      `DOCS_MODULE.DOCUMENT.${document.id ? 'UPDATE_MESSAGE' : 'CREATE_MESSAGE'}`,
    );
    const messageType = document.id ? MessageType.Update : MessageType.Create;
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '95vw';
    dialogConfig.maxWidth = '95vw';
    dialogConfig.data = { document, viewMode: false };

    const dialogRef = this.dialog.open(CreateDocumentComponent, dialogConfig);

    if (document.id)
      this.documentService
        .openDocumentSession(document.id, {
          user_type: AccessHistoryUserType.USER,
          interaction_type: AccessHistoryType.VIEW,
        })
        .pipe(take(1))
        .subscribe((res) => (sessionId = res?.data.id));

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (sessionId)
          this.documentService
            .closeDocumentSession(document.id, sessionId)
            .pipe(take(1))
            .subscribe();
        if (!res) return;

        this.displayMessage(message, messageType);
      });
  }

  canViewDocument(): boolean {
    return !!this.permissionsService.getPermission('canViewDocument');
  }

  viewDocument(event: Event, document: Document): void {
    event.stopPropagation();

    if (!this.canViewDocument()) return;

    let sessionId = 0;
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '95vw';
    dialogConfig.maxWidth = '95vw';
    dialogConfig.data = { document, viewMode: true };

    const dialogRef = this.dialog.open(CreateDocumentComponent, dialogConfig);

    this.documentService
      .openDocumentSession(document.id, {
        user_type: AccessHistoryUserType.USER,
        interaction_type: AccessHistoryType.VIEW,
      })
      .pipe(take(1))
      .subscribe((res) => (sessionId = res?.data.id));

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        if (sessionId)
          this.documentService
            .closeDocumentSession(document.id, sessionId)
            .pipe(take(1))
            .subscribe();
      });
  }

  shareDocument(event: Event, document: Document): void {
    event.stopPropagation();

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '800px';
    dialogConfig.data = document;

    const dialogRef = this.dialog.open(ShareDocumentComponent, dialogConfig);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) return;

        this.displayMessage(
          this.translate.instant('DOCS_MODULE.DOCUMENT.SHARE_MESSAGE'),
          MessageType.Create,
        );
      });
  }

  viewAccessHistory(event: Event, document: Document): void {
    event.stopPropagation();

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '95vw';
    dialogConfig.maxWidth = '95vw';
    dialogConfig.data = document;

    this.dialog.open(AccessHistoryListComponent, dialogConfig);
  }

  reloadDocumentList(): void {
    this.paginator.pageIndex = 0;

    this.loadDocumentList();
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();

    this.searchIdInput.nativeElement.value = '';
    this.searchNameInput.nativeElement.value = '';
    this.searchDescriptionInput.nativeElement.value = '';
    this.searchCategoryAutocomplete.setValue(null);
    this.searchCategoryAutocomplete.updateValueAndValidity();
    this.searchEntityAutocomplete?.setValue(null);
    this.searchEntityAutocomplete?.updateValueAndValidity();
    this.searchTypeSelect.value = [];
    this.searchCreatedDateInput.nativeElement.value = '';
    this.searchRequiresReviewSelect.value = '';
    this.searchReviewedSelect.value = '';
  }

  resetInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadDocumentList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.resetSearchBar();
    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
      setTimeout(() => this.searchIdInput.nativeElement.focus());
    } else {
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadDocumentList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();

    this.reloadDocumentList();
  }

  closeDialog(event: Event): void {
    event.stopPropagation();

    this.dialogRef.close();
  }
}
